import { parseArtisanJson, type ParsedArtisanData, type ArtisanJson } from './artisanParser';
import { buildRoastIdentity } from './roastIdentity';
import { MAX_ARTISAN_FILE_BYTES } from './uploadLimits';

/** Everything that can stop a file before it reaches the review tabs. */
export type ArtisanFileErrorCode = 'unsupported' | 'tooLarge' | 'invalidFields' | 'parse';

export interface ArtisanFileError {
    code: ArtisanFileErrorCode;
    /** Only for 'parse' — the message from JSON.parse, which names the offending position. */
    detail?: string;
}

export interface ArtisanFileContent {
    parsed: ParsedArtisanData;
    /** Stable key the backend dedupes on. Derived from the file, never per-attempt. */
    identity: string;
}

export type ReadArtisanFileResult =
    | { ok: true; content: ArtisanFileContent }
    | { ok: false; error: ArtisanFileError };

/** Artisan writes Python literals into .alog exports, which JSON.parse cannot read. */
const sanitizeAlog = (text: string) =>
    text
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');

const detailOf = (err: unknown) => (err instanceof Error ? err.message : undefined);

/**
 * Reads and parses one Artisan export in the browser. Nothing is sent anywhere — the
 * result feeds the review tabs, and only the roasts the user keeps are saved afterwards.
 */
export async function readArtisanFile(file: File): Promise<ReadArtisanFileResult> {
    if (!file.name.endsWith('.json') && !file.name.endsWith('.alog')) {
        return { ok: false, error: { code: 'unsupported' } };
    }
    if (file.size > MAX_ARTISAN_FILE_BYTES) {
        return { ok: false, error: { code: 'tooLarge' } };
    }

    let text: string;
    try {
        text = await file.text();
    } catch (err) {
        return { ok: false, error: { code: 'parse', detail: detailOf(err) } };
    }

    try {
        const json: ArtisanJson = JSON.parse(file.name.endsWith('.alog') ? sanitizeAlog(text) : text);
        if (!json.timex || !json.temp1 || !json.temp2) {
            return { ok: false, error: { code: 'invalidFields' } };
        }
        // The identity hashes the file as written, not the sanitised copy, so re-reading
        // the same export always lands on the same key.
        return { ok: true, content: { parsed: parseArtisanJson(json), identity: buildRoastIdentity(json, text) } };
    } catch (err) {
        return { ok: false, error: { code: 'parse', detail: detailOf(err) } };
    }
}
